'use client'

import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronsUpDown } from 'lucide-react'
import { cn } from '../lib/utils'

interface Vendor {
  id: string
  name: string
  email?: string
  phone?: string
}

interface VendorDropdownProps {
  vendors: Vendor[]
  value?: string
  onChange: (vendorId: string | undefined, vendorName: string | undefined) => void
  placeholder?: string
  className?: string
}

export function VendorDropdown({
  vendors,
  value,
  onChange,
  placeholder = 'Select vendor',
  className
}: VendorDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 320) // Minimum width of 320px
      })
      // Focus search input
      setTimeout(() => searchInputRef.current?.focus(), 0)
    }
  }, [isOpen])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Filter vendors based on search query
  const filteredVendors = vendors.filter((vendor) => {
    const query = searchQuery.toLowerCase()
    return (
      vendor.name.toLowerCase().includes(query) ||
      vendor.email?.toLowerCase().includes(query) ||
      vendor.phone?.toLowerCase().includes(query)
    )
  })

  // Get selected vendor
  const selectedVendor = vendors.find((v) => v.id === value)

  const handleSelect = (vendorId: string | undefined, vendorName: string | undefined) => {
    onChange(vendorId, vendorName)
    setIsOpen(false)
    setSearchQuery('')
  }

  // Dropdown content
  const dropdownContent = isOpen ? (
    <div
      ref={dropdownRef}
      style={{
        position: 'fixed',
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        width: `${dropdownPosition.width}px`,
        maxWidth: '400px',
        minWidth: '320px',
        zIndex: 9999,
        pointerEvents: 'auto'
      }}
      className="bg-white border border-slate-200 rounded-md shadow-lg"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Search Input */}
      <div className="p-2 border-b border-slate-200 bg-white">
        <input
          ref={searchInputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search vendors..."
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          autoComplete="off"
        />
      </div>

      {/* Vendor List */}
      <div className="max-h-[250px] overflow-y-auto">
        {/* None Option */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            handleSelect(undefined, undefined)
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className="w-full flex items-center px-3 py-2 text-sm hover:bg-slate-100 transition-colors text-left"
        >
          <Check
            className={cn(
              'mr-2 h-4 w-4 flex-shrink-0',
              !value ? 'opacity-100' : 'opacity-0'
            )}
          />
          <span className="text-slate-500 italic">None</span>
        </button>

        {/* Filtered Vendors */}
        {filteredVendors.length > 0 ? (
          filteredVendors.map((vendor) => (
            <button
              key={vendor.id}
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                handleSelect(vendor.id, vendor.name)
              }}
              onMouseDown={(e) => e.stopPropagation()}
              className="w-full flex items-center px-3 py-2 text-sm hover:bg-slate-100 transition-colors text-left"
            >
              <Check
                className={cn(
                  'mr-2 h-4 w-4 flex-shrink-0',
                  value === vendor.id ? 'opacity-100' : 'opacity-0'
                )}
              />
              <div className="flex flex-col items-start min-w-0">
                <span className="font-medium text-sm truncate w-full">{vendor.name}</span>
                {vendor.email && (
                  <span className="text-xs text-slate-500 truncate w-full">{vendor.email}</span>
                )}
              </div>
            </button>
          ))
        ) : (
          <div className="px-3 py-6 text-center text-sm text-slate-500">
            No vendor found.
          </div>
        )}
      </div>
    </div>
  ) : null

  return (
    <div className={cn('relative', className)}>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setIsOpen(!isOpen)
        }}
        onMouseDown={(e) => {
          e.stopPropagation()
        }}
        className="h-9 w-full flex items-center justify-between px-3 py-2 text-sm border border-slate-200 rounded-md bg-white hover:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
      >
        <span className={cn('truncate', !selectedVendor && 'text-slate-500')}>
          {selectedVendor ? selectedVendor.name : placeholder}
        </span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </button>

      {/* Portal the dropdown to body */}
      {typeof window !== 'undefined' && dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  )
}
